import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { ToastType } from '@components/Toast';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';

import {
  AddCollectionCommentPayload,
  ATGLookupValue,
  CollectionActionPayload,
  CollectionCommentResp,
  CollectionDetails,
  CollectionResponse,
  CollectionsDashboardItem,
  CollectionsRiskPercentPayload,
  CollectionsTier,
  EditCollectionCommentPayload,
  IComment,
} from './types';

export const fetchCollectionsDashboard = async () => {
  try {
    const response: V4ApiResponse<CollectionsDashboardItem[]> =
      await apiClient.get('CollectionsTier/collectionslist');
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to fetch collections queue data');
    handleToast(ToastType.Error, e.message);
  }
};

export const getCollectionsByCustomer = async (customerId: number) => {
  try {
    const response: V4ApiResponse<CollectionDetails> = await apiClient.get(
      `CollectionsTier/collectionsbycustomer?customerId=${customerId}`,
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch Collections by Customer');
    handleToast(ToastType.Error, e.message);
  }
};

export const updateCollectionsRiskPercentCall = async (
  payload: CollectionsRiskPercentPayload,
) => {
  try {
    const response: V4ApiResponse<{
      ids: number[];
      collectionsRiskPercent: number | string;
    }> = await apiClient.post(
      'CollectionsTier/updatecollectionsriskpercent',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.collectionsByCustomer.updateRiskPercent,
      ]),
    );

    return response;
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to update Collection Risk Percent. Please try again later.',
    );
    handleToast(ToastType.Error, e.message);
  }
};

export const applyCollectionActionsCall = async (
  payload: CollectionActionPayload[],
) => {
  try {
    const response: V4ApiResponse<CollectionResponse> = await apiClient.post(
      'CollectionsTier/applycollectionActions',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.collectionsByCustomer.applyActions,
      ]),
    );
    if ('value' in response) {
      return response;
    }
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to apply Collection Actions. Please try again later.',
    );
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchCollectionsTierCall = async () => {
  try {
    const response: V4ApiResponse<CollectionsTier[]> =
      await apiClient.get('CollectionsTier');
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch Collections Tier');
    handleToast(ToastType.Error, e.message);
  }
};

export const updateCollectionsTierCall = async (payload: {
  id: string;
  tierId: string;
}) => {
  try {
    const response: V4ApiResponse<{
      id: string;
      tierId: string;
    }> = await apiClient.put(
      `CollectionsTier/customertier/${payload.id}`,
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.collectionsByCustomer.assignTier,
      ]),
    );
    if ('value' in response) {
      return response;
    }
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to update collection tier. Please try again later.',
    );
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchATGLookupValuesCall = async () => {
  try {
    const data: V4ApiResponse<ATGLookupValue[]> =
      await apiClient.get('/atglookupvalue');
    if ('value' in data) {
      return data.value;
    }
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch Key Reason data');
    handleToast(ToastType.Error, e.message);
  }
};

export const updateCollectionsKeyReasonCall = async (payload: {
  id: number;
  keyReasonId: number;
}) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.put(
      `Customer/CollectionsKeyReason/${payload.id}`,
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.collectionsByCustomer.assignKeyReason,
      ]),
    );
    return response;
  } catch (error: unknown) {
    if (error instanceof Error) {
      handleToast(
        ToastType.Error,
        'Failed to update collection key reason. Please try again later.',
      );
    }
  }
};

export const updateNextActionCall = async (payload: {
  id: string;
  stringDate: string | null;
}) => {
  try {
    const response: V4ApiResponse<CollectionResponse> = await apiClient.put(
      `Customer/NextAction/${payload.id}`,
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.collectionsByCustomer.updateNextActionDate,
      ]),
    );
    return response;
  } catch (error: unknown) {
    handleToast(
      ToastType.Error,
      'Failed to update next action. Please try again later.',
    );
  }
};

export const getCollectionCommentsCall = async (customerId: number) => {
  try {
    const response: V4ApiResponse<IComment[]> = await apiClient.get(
      'CollectionsTier/getcollectioncomments',
      {
        params: {
          customerId,
        },
      },
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch Collection Comments');
    handleToast(ToastType.Error, e.message);
  }
};

export const deleteCollectionCommentCall = async (commentId: number) => {
  try {
    const response: { isSuccess: boolean } = await apiClient.delete(
      `Customer/Comment/${commentId}`,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.collectionsByCustomer.deleteComment,
      ]),
    );
    return response;
  } catch (error: unknown) {
    handleToast(
      ToastType.Error,
      'Failed to delete Collection Comment. Please try again later.',
    );
  }
};

export const addCollectionCommentCall = async (
  payload: AddCollectionCommentPayload,
) => {
  try {
    const response: V4ApiResponse<CollectionCommentResp> = await apiClient.post(
      'Customer/comment',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.collectionsByCustomer.addComment,
      ]),
    );
    return response;
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to add Collection Comment. Please try again later.',
    );
    handleToast(ToastType.Error, e.message);
  }
};

export const sendToCollectionsCall = async (invoiceIds: number[]) => {
  try {
    const response: V4ApiResponse<{ orderIds: number[] }> =
      await apiClient.post('CollectionsTier/sendtocollections', {
        orderIds: invoiceIds,
      });
    return response;
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to send invoices to Collections. Please try again later.',
    );
    handleToast(ToastType.Error, e.message);
  }
};

export const editCollectionCommentCall = async (
  payload: EditCollectionCommentPayload,
) => {
  try {
    const response: V4ApiResponse<CollectionCommentResp> = await apiClient.put(
      'Customer/Comment',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.collectionsByCustomer.editComment,
      ]),
    );
    return response;
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to edit Collection Comment. Please try again later.',
    );
    handleToast(ToastType.Error, e.message);
  }
};
